'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getConsent, saveConsent, logConsentAction, type ConsentRegion } from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  region: ConsentRegion;
  onClose: () => void;
  onSave: () => void;
}

export default function ConsentPreferenceCenter({
  region,
  onClose,
  onSave,
}: ConsentPreferenceCenterProps) {
  const [choices, setChoices] = useState<{
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  }>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  // Load current consent on mount
  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      setChoices(existing.choices);
    }
  }, []);

  // Focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift+Tab on first element
          if (document.activeElement === firstFocusableRef.current) {
            e.preventDefault();
            lastFocusableRef.current?.focus();
          }
        } else {
          // Tab on last element
          if (document.activeElement === lastFocusableRef.current) {
            e.preventDefault();
            firstFocusableRef.current?.focus();
          }
        }
      }
    };

    dialogRef.current?.addEventListener('keydown', handleKeyDown);
    return () => dialogRef.current?.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus dialog on mount
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleToggle = (category: 'analytics' | 'marketing' | 'preferences') => {
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const acceptAll: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  } = {
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true,
  };

  const handleSave = () => {
    saveConsent(choices, region);
    logConsentAction('preferences_saved', region, choices);
    onSave();
  };

  const handleAcceptAll = () => {
    setChoices(acceptAll);
    saveConsent(acceptAll, region);
    logConsentAction('accept_all', region);
    onSave();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <h2 id="preferences-title" className="text-lg font-semibold text-foreground mb-4">
              Cookie Preferences
            </h2>

            <div className="space-y-4 mb-6">
              {/* Necessary - Always ON, locked */}
              <div className="p-4 bg-surface border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Necessary</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Essential for site functionality, security, and authentication. Always
                      enabled.
                    </p>
                  </div>
                  <button
                    role="switch"
                    aria-checked="true"
                    disabled
                    className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full bg-primary cursor-not-allowed opacity-60"
                    title="Necessary cookies cannot be disabled"
                  >
                    <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow-lg translate-x-5" />
                  </button>
                </div>
              </div>

              {/* Analytics */}
              <div className="p-4 bg-surface border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Analytics</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Help us understand how you use the site to improve your experience.
                    </p>
                  </div>
                  <button
                    ref={firstFocusableRef}
                    role="switch"
                    aria-checked={choices.analytics}
                    onClick={() => handleToggle('analytics')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                      choices.analytics ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                        choices.analytics ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Marketing */}
              <div className="p-4 bg-surface border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Marketing</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enable personalized ads and retargeting based on your interests.
                    </p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={choices.marketing}
                    onClick={() => handleToggle('marketing')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                      choices.marketing ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                        choices.marketing ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Preferences */}
              <div className="p-4 bg-surface border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Preferences</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Remember your preferences for language, theme, and other settings.
                    </p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={choices.preferences}
                    onClick={() => handleToggle('preferences')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                      choices.preferences ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                        choices.preferences ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSave}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                Save Preferences
              </button>
              <button
                ref={lastFocusableRef}
                onClick={handleAcceptAll}
                className="w-full px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
